import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import SettingsSectionNav from '../../components/admin/settings/SettingsSectionNav';
import { SettingsPanel, SettingsField, SettingsSaveButton } from '../../components/admin/settings/SettingsPanel';
import PaymentProviderSwitcher, { getPaymentProviderMeta } from '../../components/admin/settings/PaymentProviderSwitcher';
import {
  DEFAULT_SETTINGS_SECTION,
  findSectionById,
  getVisibleSettingsSections
} from '../../components/admin/settings/settingsSections';
import { adminAPI, downloadDatabaseBackup, downloadSavedDatabaseBackup, listDatabaseBackups, startSiteBackup, cancelSiteBackup, getSiteBackupStatus, downloadSiteBackup } from '../../lib/api';
import { Download, Loader2 } from 'lucide-react';
import { isAdminRole, isSuperAdminRole } from '../../utils/roles';

function readSectionFromHash() {
  const hash = window.location.hash.replace('#', '');
  return hash || DEFAULT_SETTINGS_SECTION;
}

const SettingsPage = () => {
  const { user } = useAuth();
  const [navSidebarOpen, setNavSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(readSectionFromHash);
  const [backingUp, setBackingUp] = useState(false);
  const [savedBackups, setSavedBackups] = useState([]);
  const [backupDirectory, setBackupDirectory] = useState('');
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [siteBackupStatus, setSiteBackupStatus] = useState(null);
  const [siteBackupLatest, setSiteBackupLatest] = useState(null);
  const [siteBackupRoot, setSiteBackupRoot] = useState('');
  const [startingSiteBackup, setStartingSiteBackup] = useState(false);
  const [cancellingSiteBackup, setCancellingSiteBackup] = useState(false);
  const [downloadingSiteBackup, setDownloadingSiteBackup] = useState(false);
  const [downloadingSqlFilename, setDownloadingSqlFilename] = useState(null);
  const siteBackupNotifyRef = useRef(null);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [paymentProvider, setPaymentProvider] = useState('MPESA_DARAJA');
  const [mpesaConfigured, setMpesaConfigured] = useState(false);
  const [mpesaStkCallbackUrl, setMpesaStkCallbackUrl] = useState('');
  const [mpesaSettings, setMpesaSettings] = useState({
    env: 'sandbox',
    driver: 'live',
    consumerKey: '',
    consumerSecret: '',
    shortCode: '174379',
    passkey: ''
  });
  const [savingMpesa, setSavingMpesa] = useState(false);
  const [testingMpesa, setTestingMpesa] = useState(false);
  const [familyBankStkCallbackUrl, setFamilyBankStkCallbackUrl] = useState('');
  const [familyBankIpnCallbackUrl, setFamilyBankIpnCallbackUrl] = useState('');
  const [familyBankConfigured, setFamilyBankConfigured] = useState(false);
  const [familyBankSettings, setFamilyBankSettings] = useState({
    env: 'production',
    driver: 'live',
    clientId: '',
    clientSecret: '',
    businessShortCode: ''
  });
  const [savingFamilyBank, setSavingFamilyBank] = useState(false);
  const [ncbaConfigured, setNcbaConfigured] = useState(false);
  const [ncbaReadyForStk, setNcbaReadyForStk] = useState(false);
  const [ncbaSettings, setNcbaSettings] = useState({
    env: 'production',
    driver: 'live',
    baseUrl: 'https://c2bapis.ncbagroup.com',
    username: '',
    secret: '',
    paybillNo: '880100',
    accountNo: '',
    tillNumber: '',
    network: 'Safaricom',
    transactionType: 'CustomerPayBillOnline',
    credentialsGeneratedAt: null,
    hasCredentials: false,
    hasTill: false
  });
  const [ncbaPlainCredentials, setNcbaPlainCredentials] = useState(null);
  const [savingNcba, setSavingNcba] = useState(false);
  const [generatingNcba, setGeneratingNcba] = useState(false);
  const [revealingNcba, setRevealingNcba] = useState(false);
  const [testingNcba, setTestingNcba] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({
    host: '84.16.249.171',
    port: 465,
    secure: true,
    user: 'tickets@nikovibe.com',
    pass: '',
    from: 'tickets@nikovibe.com',
    fromName: 'Niko Vibe',
    replyTo: 'info@nikovibe.com',
    tlsServername: 'mail.nikovibe.com'
  });
  const [smtpTestTo, setSmtpTestTo] = useState('');
  const [savingMapsKey, setSavingMapsKey] = useState(false);
  const [savingPaymentProvider, setSavingPaymentProvider] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const isSuperAdmin = isSuperAdminRole(user?.role);
  const isAdmin = isAdminRole(user?.role);
  const roleResolved = Boolean(user?.role);

  const visibleSections = useMemo(
    () => (roleResolved ? getVisibleSettingsSections({ isAdmin, isSuperAdmin }) : []),
    [isAdmin, isSuperAdmin, roleResolved]
  );

  const flatSectionIds = useMemo(
    () => visibleSections.flatMap((g) => g.items.map((i) => i.id)),
    [visibleSections]
  );

  const selectSection = useCallback((id) => {
    setActiveSection(id);
    window.history.replaceState(null, '', `#${id}`);
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const next = readSectionFromHash();
      if (flatSectionIds.includes(next)) {
        setActiveSection(next);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [flatSectionIds]);

  useEffect(() => {
    if (!flatSectionIds.includes(activeSection) && flatSectionIds.length > 0) {
      selectSection(flatSectionIds[0]);
    }
  }, [activeSection, flatSectionIds, selectSection]);

  const loadSavedBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await listDatabaseBackups();
      if (response?.success) {
        setSavedBackups(response.data.backups || []);
        setBackupDirectory(response.data.directory || '');
      }
    } catch {
      // Non-blocking
    } finally {
      setLoadingBackups(false);
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      setLoadingSettings(true);
      try {
        const response = await adminAPI.getSettings({ skipAuthRedirect: true });
        if (response?.success) {
          setGoogleMapsApiKey(response.data.settings?.google_maps_api_key || '');
          setPaymentProvider(response.data.settings?.payment_provider || 'MPESA_DARAJA');
          setMpesaConfigured(Boolean(response.data.settings?.mpesa_configured));
          if (response.data.settings?.smtp_settings) {
            setSmtpSettings((prev) => ({
              ...prev,
              ...response.data.settings.smtp_settings
            }));
          }
          if (response.data.settings?.family_bank_settings) {
            setFamilyBankSettings((prev) => ({
              ...prev,
              ...response.data.settings.family_bank_settings
            }));
          }
          if (response.data.settings?.mpesa_settings) {
            setMpesaSettings((prev) => ({
              ...prev,
              ...response.data.settings.mpesa_settings
            }));
          }
          setFamilyBankStkCallbackUrl(response.data.settings?.family_bank_stk_callback_url || '');
          setFamilyBankIpnCallbackUrl(response.data.settings?.family_bank_ipn_callback_url || '');
          setMpesaStkCallbackUrl(response.data.settings?.mpesa_stk_callback_url || '');
          setFamilyBankConfigured(Boolean(response.data.settings?.family_bank_configured));
          setNcbaConfigured(Boolean(response.data.settings?.ncba_configured));
          setNcbaReadyForStk(Boolean(response.data.settings?.ncba_ready_for_stk));
          if (response.data.settings?.ncba_settings) {
            setNcbaSettings((prev) => ({
              ...prev,
              ...response.data.settings.ncba_settings
            }));
          }
        }
      } catch {
        // Non-blocking
      } finally {
        setLoadingSettings(false);
      }
    };
    loadSettings();
  }, []);

  const loadSiteBackupStatus = async ({ silent = false } = {}) => {
    try {
      const response = await getSiteBackupStatus();
      if (response?.success) {
        setSiteBackupStatus(response.data.status || null);
        setSiteBackupLatest(response.data.latest || null);
        setSiteBackupRoot(response.data.backupsDirectory || response.data.domainRoot || '');
        return response.data;
      }
    } catch (error) {
      if (!silent) {
        toast.error(error.message || 'Failed to load site backup status');
      }
    }
    return null;
  };

  useEffect(() => {
    if (isSuperAdmin && activeSection === 'backup') {
      loadSavedBackups();
      loadSiteBackupStatus({ silent: true });
    }
  }, [isSuperAdmin, activeSection]);

  useEffect(() => {
    const jobStatus = siteBackupStatus?.status;
    if (!isSuperAdmin || activeSection !== 'backup') return undefined;
    if (jobStatus !== 'running' && jobStatus !== 'queued') return undefined;

    const intervalId = window.setInterval(async () => {
      const data = await loadSiteBackupStatus({ silent: true });
      const nextStatus = data?.status?.status;
      if (nextStatus === siteBackupNotifyRef.current) return;

      if (nextStatus === 'completed') {
        siteBackupNotifyRef.current = 'completed';
        toast.success(`Site backup ready: ${data.status.filename || 'download below'}`);
      } else if (nextStatus === 'failed') {
        siteBackupNotifyRef.current = 'failed';
        toast.error(data.status.error || data.status.message || 'Site backup failed');
      }
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [isSuperAdmin, activeSection, siteBackupStatus?.status]);

  useEffect(() => {
    const jobStatus = siteBackupStatus?.status;
    if (jobStatus === 'running' || jobStatus === 'queued') {
      siteBackupNotifyRef.current = null;
    }
  }, [siteBackupStatus?.status]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSaveMapsKey = async () => {
    setSavingMapsKey(true);
    try {
      await adminAPI.updateSettings({
        settings: { google_maps_api_key: googleMapsApiKey.trim() }
      });
      toast.success('Google Maps API key saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save API key');
    } finally {
      setSavingMapsKey(false);
    }
  };

  const handleSaveMpesa = async () => {
    setSavingMpesa(true);
    try {
      await adminAPI.updateSettings({
        settings: { mpesa_settings: mpesaSettings }
      });
      toast.success('M-Pesa Daraja settings saved to database');
      setMpesaSettings((prev) => ({
        ...prev,
        consumerSecret: '********',
        passkey: '********'
      }));
      setMpesaConfigured(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save M-Pesa settings');
    } finally {
      setSavingMpesa(false);
    }
  };

  const handleTestMpesa = async () => {
    setTestingMpesa(true);
    try {
      const res = await adminAPI.testMpesaConnection();
      if (res?.success) {
        toast.success(res.message || 'Daraja OAuth connection OK');
      }
    } catch (error) {
      toast.error(error.message || 'Daraja connection test failed');
    } finally {
      setTestingMpesa(false);
    }
  };

  const handleSaveFamilyBank = async () => {
    setSavingFamilyBank(true);
    try {
      await adminAPI.updateSettings({
        settings: { family_bank_settings: familyBankSettings }
      });
      toast.success('Family Bank settings saved to database');
      setFamilyBankSettings((prev) => ({ ...prev, clientSecret: '********' }));
      setFamilyBankConfigured(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save Family Bank settings');
    } finally {
      setSavingFamilyBank(false);
    }
  };

  const handleSaveNcba = async () => {
    setSavingNcba(true);
    try {
      await adminAPI.updateSettings({
        settings: {
          ncba_settings: {
            ...ncbaSettings,
            accountNo: ncbaSettings.accountNo || ncbaSettings.tillNumber,
            tillNumber: ncbaSettings.tillNumber || ncbaSettings.accountNo
          }
        }
      });
      toast.success('NCBA Till settings saved');
      setNcbaSettings((prev) => ({
        ...prev,
        secret: prev.secret ? '********' : '',
        hasTill: Boolean(prev.accountNo || prev.tillNumber)
      }));
      setNcbaReadyForStk(Boolean(
        (ncbaSettings.hasCredentials || ncbaSettings.username)
        && (ncbaSettings.accountNo || ncbaSettings.tillNumber)
      ));
      setNcbaConfigured(Boolean(ncbaSettings.hasCredentials || ncbaSettings.username));
    } catch (error) {
      toast.error(error.message || 'Failed to save NCBA settings');
    } finally {
      setSavingNcba(false);
    }
  };

  const handleGenerateNcba = async () => {
    if (ncbaSettings.hasCredentials || ncbaSettings.username) {
      const ok = window.confirm(
        'This will replace the existing Username and Secret Key. You will need to resubmit them to NCBA. Continue?'
      );
      if (!ok) return;
    }
    setGeneratingNcba(true);
    try {
      const response = await adminAPI.generateNcbaCredentials({
        paybillNo: ncbaSettings.paybillNo || '880100',
        accountNo: ncbaSettings.accountNo || ncbaSettings.tillNumber || '',
        tillNumber: ncbaSettings.tillNumber || ncbaSettings.accountNo || '',
        baseUrl: ncbaSettings.baseUrl,
        driver: ncbaSettings.driver,
        env: ncbaSettings.env
      });
      const data = response?.data || response;
      setNcbaPlainCredentials({
        username: data.username,
        secret: data.secret,
        generatedAt: data.generatedAt
      });
      if (data.settings) {
        setNcbaSettings((prev) => ({ ...prev, ...data.settings, hasCredentials: true }));
      }
      setNcbaConfigured(true);
      toast.success('Username and Secret Key generated — copy them for the bank email now');
    } catch (error) {
      toast.error(error.message || 'Failed to generate NCBA credentials');
    } finally {
      setGeneratingNcba(false);
    }
  };

  const handleRevealNcba = async () => {
    setRevealingNcba(true);
    try {
      const response = await adminAPI.revealNcbaCredentials();
      const data = response?.data || response;
      setNcbaPlainCredentials({
        username: data.username,
        secret: data.secret,
        generatedAt: data.generatedAt
      });
      toast.success('Credentials revealed for bank letter/email');
    } catch (error) {
      toast.error(error.message || 'Failed to reveal NCBA credentials');
    } finally {
      setRevealingNcba(false);
    }
  };

  const handleTestNcba = async () => {
    setTestingNcba(true);
    try {
      const response = await adminAPI.testNcbaConnection();
      toast.success(response?.message || 'NCBA connection OK');
    } catch (error) {
      toast.error(error.message || 'NCBA connection failed');
    } finally {
      setTestingNcba(false);
    }
  };

  const copyCallbackUrl = async (url, label) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy - select and copy manually');
    }
  };

  const handlePaymentProviderChange = async (nextProvider) => {
    setSavingPaymentProvider(true);
    try {
      await adminAPI.updateSettings({
        settings: { payment_provider: nextProvider }
      });
      setPaymentProvider(nextProvider);
      toast.success(`Default checkout payment set to ${getPaymentProviderMeta(nextProvider).label}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update default payment method');
      throw error;
    } finally {
      setSavingPaymentProvider(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await adminAPI.updateSettings({
        settings: { smtp_settings: smtpSettings }
      });
      toast.success('SMTP settings saved to database');
      setSmtpSettings((prev) => ({ ...prev, pass: '********' }));
    } catch (error) {
      toast.error(error.message || 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const to = smtpTestTo.trim() || user?.email;
      const response = await adminAPI.testSmtpEmail(to);
      toast.success(response?.message || 'Test email sent');
    } catch (error) {
      toast.error(error.message || 'Failed to send test email');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleDatabaseBackup = async () => {
    setBackingUp(true);
    try {
      const { filename, savedPath } = await downloadDatabaseBackup();
      const savedMsg = savedPath ? ' Saved on server in backups/.' : '';
      toast.success(`Backup downloaded: ${filename}.${savedMsg}`);
      await loadSavedBackups();
    } catch (error) {
      toast.error(error.message || 'Failed to create database backup');
    } finally {
      setBackingUp(false);
    }
  };

  const handleStartSiteBackup = async () => {
    setStartingSiteBackup(true);
    try {
      const response = await startSiteBackup();
      if (response?.success) {
        setSiteBackupStatus(response.data.status || null);
        toast.success('Site backup started - you can keep working while it runs');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to start site backup');
      await loadSiteBackupStatus({ silent: true });
    } finally {
      setStartingSiteBackup(false);
    }
  };

  const handleDownloadSiteBackup = async (filename) => {
    setDownloadingSiteBackup(true);
    try {
      const { filename: resolvedName } = await downloadSiteBackup(filename);
      toast.success(`Download started: ${resolvedName}`);
    } catch (error) {
      toast.error(error.message || 'Failed to download site backup');
    } finally {
      setDownloadingSiteBackup(false);
    }
  };

  const handleCancelSiteBackup = async () => {
    setCancellingSiteBackup(true);
    try {
      const response = await cancelSiteBackup();
      if (response?.success) {
        setSiteBackupStatus(response.data.status || null);
        siteBackupNotifyRef.current = 'failed';
        toast.success('Backup cancelled - you can start a new one');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to cancel backup');
      await loadSiteBackupStatus({ silent: true });
    } finally {
      setCancellingSiteBackup(false);
    }
  };

  const handleDownloadSqlBackup = async (filename) => {
    setDownloadingSqlFilename(filename);
    try {
      const { filename: resolvedName } = await downloadSavedDatabaseBackup(filename);
      toast.success(`Download started: ${resolvedName}`);
    } catch (error) {
      toast.error(error.message || 'Failed to download database backup');
    } finally {
      setDownloadingSqlFilename(null);
    }
  };

  const siteBackupRunning = siteBackupStatus?.status === 'running' || siteBackupStatus?.status === 'queued';
  const siteBackupReady = Boolean(siteBackupLatest?.filename) && !siteBackupRunning;

  const sectionMeta = findSectionById(activeSection, { isAdmin, isSuperAdmin });

  const renderMapsPanel = () => (
    <SettingsPanel
      title="Google Maps"
      description="Enable venue search and autocomplete when creating physical events. Without a key, admins can still enter locations manually."
      badge="Integrations"
    >
      <SettingsField
        label="Google Maps API key"
        hint={<>Enable <strong>Maps JavaScript API</strong> and <strong>Places API</strong> in Google Cloud Console. Restrict the key to your domain in production.</>}
      >
        <input
          id="google-maps-api-key"
          type="password"
          value={googleMapsApiKey}
          onChange={(e) => setGoogleMapsApiKey(e.target.value)}
          placeholder="Paste your Maps JavaScript API key"
          disabled={loadingSettings}
          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
          autoComplete="off"
        />
      </SettingsField>
      <div className="mt-6 pt-6 border-t border-gray-100">
        <SettingsSaveButton onClick={handleSaveMapsKey} disabled={loadingSettings} loading={savingMapsKey}>
          Save API key
        </SettingsSaveButton>
      </div>
    </SettingsPanel>
  );

  const renderMailPanel = () => {
    if (!isSuperAdmin) {
      return (
        <SettingsPanel
          title="SMTP settings"
          description="Email delivery configuration for ticket confirmations and notifications."
          badge="Super admin only"
        >
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
            <p className="font-medium">Access restricted</p>
            <p className="mt-2">
              Only super admins can view or edit SMTP settings. Your role:{' '}
              <strong>{user?.role || 'unknown'}</strong>.
            </p>
          </div>
        </SettingsPanel>
      );
    }

    return (
      <SettingsPanel
        title="SMTP settings"
        description="Ticket confirmations and notifications are sent through your mail server. Credentials are stored securely in the database - not in .env files."
        badge="Super admin"
      >
        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
          Mail is hosted separately from the website. Use SMTP host <strong>84.16.249.171</strong>{' '}
          (shared hosting mail server). TLS name: <strong>mail.nikovibe.com</strong>.
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <SettingsField label="SMTP host" hint="Use 84.16.249.171 - mail.nikovibe.com points to the website server, not mail.">
            <input
              type="text"
              value={smtpSettings.host}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Port">
            <input
              type="number"
              value={smtpSettings.port}
              onChange={(e) => setSmtpSettings({
                ...smtpSettings,
                port: Number(e.target.value),
                secure: Number(e.target.value) === 465
              })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Username">
            <input
              type="email"
              value={smtpSettings.user}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, user: e.target.value, from: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Password">
            <input
              type="password"
              value={smtpSettings.pass}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, pass: e.target.value })}
              placeholder="Leave blank to keep existing"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="new-password"
            />
          </SettingsField>
          <SettingsField label="From name">
            <input
              type="text"
              value={smtpSettings.fromName}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, fromName: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Reply-To" hint="Customer replies to ticket emails are sent to this address.">
            <input
              type="email"
              value={smtpSettings.replyTo}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, replyTo: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
          <SettingsSaveButton variant="accent" onClick={handleSaveSmtp} disabled={loadingSettings} loading={savingSmtp}>
            Save SMTP settings
          </SettingsSaveButton>
          <input
            type="email"
            value={smtpTestTo}
            onChange={(e) => setSmtpTestTo(e.target.value)}
            placeholder={user?.email || 'Test recipient email'}
            className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
          />
          <SettingsSaveButton variant="outline" onClick={handleTestSmtp} disabled={testingSmtp || loadingSettings}>
            {testingSmtp ? 'Sending…' : 'Send test email'}
          </SettingsSaveButton>
        </div>
      </SettingsPanel>
    );
  };

  const renderPaymentsPanel = () => {
    const activeMeta = getPaymentProviderMeta(paymentProvider);

    return (
      <SettingsPanel
        title="Default payment method"
        description="Platform gateway for standard events (Daraja or Family Bank). Event-specific gateways like NCBA Till are configured per event and ignore this switcher."
        badge={activeMeta.label}
      >
        <PaymentProviderSwitcher
          value={paymentProvider}
          onChange={handlePaymentProviderChange}
          disabled={loadingSettings}
          saving={savingPaymentProvider}
          mpesaConfigured={mpesaConfigured}
          familyBankConfigured={familyBankConfigured}
        />
      </SettingsPanel>
    );
  };

  const renderMpesaDarajaPanel = () => {
    const darajaBaseUrl = mpesaSettings.env === 'production'
      ? 'https://api.safaricom.co.ke'
      : 'https://sandbox.safaricom.co.ke';

    if (!isSuperAdmin) {
      return (
        <SettingsPanel
          title="M-Pesa Daraja STK"
          description="Safaricom Daraja API for M-Pesa STK Push."
          badge="Restricted"
        >
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
            <p className="font-medium">Super admin access required</p>
          </div>
        </SettingsPanel>
      );
    }

    return (
      <SettingsPanel
        title="M-Pesa Daraja STK"
        description="Paste your Safaricom Daraja credentials here (stored in the database). STK callbacks fulfill ticket orders. Use Admin → Payments → Query STK to reconcile pending transactions."
        badge={mpesaConfigured ? 'Configured' : 'Awaiting credentials'}
      >
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-950">
          <p className="font-semibold mb-1">STK callback URL</p>
          <p className="text-green-900/90 mb-3">
            Register this URL in the Safaricom Daraja portal (Lipa na M-Pesa Online). Successful callbacks complete ticket orders.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 rounded-lg bg-white/80 border border-green-200 px-3 py-2 text-xs font-mono break-all">
              {mpesaStkCallbackUrl || 'https://api.nikovibe.com/api/payments/mpesa/callback'}
            </code>
            <button
              type="button"
              onClick={() => copyCallbackUrl(mpesaStkCallbackUrl, 'M-Pesa callback URL')}
              className="shrink-0 rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-800 hover:bg-green-100 transition-colors"
            >
              Copy callback URL
            </button>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <SettingsField label="Environment">
            <select
              value={mpesaSettings.env}
              onChange={(e) => {
                const env = e.target.value;
                setMpesaSettings({
                  ...mpesaSettings,
                  env,
                  shortCode: env === 'sandbox' ? '174379' : mpesaSettings.shortCode,
                  passkey: env === 'sandbox' ? 'bfb279f9aa9bdbcf158e97dd71a467cd2e0c893059b10f78e6b72ada1ed2c919' : mpesaSettings.passkey
                });
              }}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            >
              <option value="sandbox">Sandbox (testing)</option>
              <option value="production">Production (live)</option>
            </select>
          </SettingsField>
          <SettingsField label="Driver" hint="Mock skips HTTP for local testing without Safaricom credentials.">
            <select
              value={mpesaSettings.driver || 'live'}
              onChange={(e) => setMpesaSettings({ ...mpesaSettings, driver: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            >
              <option value="live">Live</option>
              <option value="mock">Mock (no HTTP)</option>
            </select>
          </SettingsField>
          <SettingsField
            label="Daraja base URL"
            hint="Set automatically from environment."
          >
            <div className="text-xs font-mono text-gray-600 bg-gray-50 rounded-xl border border-gray-100 p-3 break-all">
              {darajaBaseUrl}
            </div>
          </SettingsField>
          <SettingsField label="Business short code (paybill / till)" hint="Sandbox default: 174379">
            <input
              type="text"
              value={mpesaSettings.shortCode}
              onChange={(e) => setMpesaSettings({ ...mpesaSettings, shortCode: e.target.value })}
              placeholder="From Safaricom Daraja"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Consumer key">
            <input
              type="text"
              value={mpesaSettings.consumerKey}
              onChange={(e) => setMpesaSettings({ ...mpesaSettings, consumerKey: e.target.value })}
              placeholder="Daraja app consumer key"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="off"
            />
          </SettingsField>
          <SettingsField label="Consumer secret">
            <input
              type="password"
              value={mpesaSettings.consumerSecret}
              onChange={(e) => setMpesaSettings({ ...mpesaSettings, consumerSecret: e.target.value })}
              placeholder="Leave blank to keep existing"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="new-password"
            />
          </SettingsField>
          <SettingsField label="Lipa na M-Pesa passkey" hint="Online passkey from Daraja portal (not the consumer secret).">
            <input
              type="password"
              value={mpesaSettings.passkey}
              onChange={(e) => setMpesaSettings({ ...mpesaSettings, passkey: e.target.value })}
              placeholder="Leave blank to keep existing"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="new-password"
            />
          </SettingsField>
        </div>

        <p className="mt-4 text-xs text-gray-500">
          Bill references use format <code className="bg-gray-100 px-1 rounded">NVIBE20260710R4M</code> (year + month/day + 3-char code).
          Sandbox test phone: <strong>254708374149</strong>. After saving credentials, set <strong>Default payment method</strong> to M-Pesa STK.
        </p>

        <div className="mt-6 pt-6 border-t border-gray-100 flex flex-wrap gap-3">
          <SettingsSaveButton
            variant="accent"
            onClick={handleSaveMpesa}
            disabled={loadingSettings}
            loading={savingMpesa}
          >
            Save M-Pesa settings
          </SettingsSaveButton>
          <button
            type="button"
            onClick={handleTestMpesa}
            disabled={loadingSettings || testingMpesa}
            className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {testingMpesa ? 'Testing OAuth…' : 'Test Daraja connection'}
          </button>
        </div>
      </SettingsPanel>
    );
  };

  const renderFamilyBankPanel = () => {
    if (!isSuperAdmin) {
      return (
        <SettingsPanel
          title="Family Bank STK"
          description="M-Pesa STK push via Family Bank Open Banking."
          badge="Restricted"
        >
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
            <p className="font-medium">Super admin access required</p>
          </div>
        </SettingsPanel>
      );
    }

    return (
      <SettingsPanel
        title="Family Bank STK"
        description="Paste your Family Bank credentials here (stored in the database). STK callbacks fulfill ticket orders; all STK and C2B IPN notifications appear in Admin → Family Bank IPN."
        badge={familyBankConfigured ? 'Configured' : 'Awaiting credentials'}
      >
        <div className="mb-4 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-950">
          <p className="font-semibold mb-1">STK callback URL - ticket payments</p>
          <p className="text-orange-900/90 mb-3">
            Whitelist for STK Push. Successful STK callbacks complete ticket orders.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 rounded-lg bg-white/80 border border-orange-200 px-3 py-2 text-xs font-mono break-all">
              {familyBankStkCallbackUrl || 'https://api.nikovibe.com/api/payments/family-bank/stk-callback'}
            </code>
            <button
              type="button"
              onClick={() => copyCallbackUrl(familyBankStkCallbackUrl, 'STK callback URL')}
              className="shrink-0 rounded-lg border border-orange-300 bg-white px-4 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-100 transition-colors"
            >
              Copy STK URL
            </button>
          </div>
        </div>

        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
          <p className="font-semibold mb-1">IPN callback URL - record keeping</p>
          <p className="text-blue-900/90 mb-3">
            Whitelist for C2B Instant Payment Notifications. Stored for audit only - does not fulfill orders. STK results also appear in the IPN log (tagged STK Push).
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 rounded-lg bg-white/80 border border-blue-200 px-3 py-2 text-xs font-mono break-all">
              {familyBankIpnCallbackUrl || 'https://api.nikovibe.com/api/payments/family-bank/ipn-callback'}
            </code>
            <button
              type="button"
              onClick={() => copyCallbackUrl(familyBankIpnCallbackUrl, 'IPN callback URL')}
              className="shrink-0 rounded-lg border border-blue-300 bg-white px-4 py-2 text-sm font-semibold text-blue-800 hover:bg-blue-100 transition-colors"
            >
              Copy IPN URL
            </button>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <SettingsField label="Environment">
            <select
              value={familyBankSettings.env}
              onChange={(e) => {
                const env = e.target.value;
                const urls = env === 'production'
                  ? {
                    tokenUrl: 'https://openbank.familybank.co.ke:8083/connect/token',
                    stkUrl: 'https://openbank.familybank.co.ke:8084/api/v1/Mpesa/stkpush'
                  }
                  : {
                    tokenUrl: 'https://sandbox.familybank.co.ke/connect/token',
                    stkUrl: 'https://sandbox.familybank.co.ke/api/v1/Mpesa/stkpush'
                  };
                setFamilyBankSettings({ ...familyBankSettings, env, ...urls, useCustomUrls: false });
              }}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            >
              <option value="sandbox">Sandbox (testing)</option>
              <option value="production">Production (live)</option>
            </select>
          </SettingsField>
          <SettingsField label="Driver" hint="Use Live with real Family Bank credentials. Mock skips HTTP for local testing.">
            <select
              value={familyBankSettings.driver || 'live'}
              onChange={(e) => setFamilyBankSettings({ ...familyBankSettings, driver: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            >
              <option value="live">Live</option>
              <option value="mock">Mock (no HTTP)</option>
            </select>
          </SettingsField>
          <SettingsField
            label="API endpoints"
            hint="Set automatically from environment. Production credentials must use Production URLs."
          >
            <div className="space-y-2 text-xs font-mono text-gray-600 bg-gray-50 rounded-xl border border-gray-100 p-3 break-all">
              <p><span className="text-gray-400">Token:</span> {familyBankSettings.tokenUrl || '-'}</p>
              <p><span className="text-gray-400">STK:</span> {familyBankSettings.stkUrl || '-'}</p>
            </div>
          </SettingsField>
          <SettingsField label="Business short code (paybill)" hint="AccountReference prefix, e.g. 604020 → 604020#NVIBE20260710R4M">
            <input
              type="text"
              value={familyBankSettings.businessShortCode}
              onChange={(e) => setFamilyBankSettings({ ...familyBankSettings, businessShortCode: e.target.value })}
              placeholder="From Family Bank"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Client ID">
            <input
              type="text"
              value={familyBankSettings.clientId}
              onChange={(e) => setFamilyBankSettings({ ...familyBankSettings, clientId: e.target.value })}
              placeholder="OAuth client ID"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="off"
            />
          </SettingsField>
          <SettingsField label="Client secret">
            <input
              type="password"
              value={familyBankSettings.clientSecret}
              onChange={(e) => setFamilyBankSettings({ ...familyBankSettings, clientSecret: e.target.value })}
              placeholder="Leave blank to keep existing"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="new-password"
            />
          </SettingsField>
        </div>

        <p className="mt-4 text-xs text-gray-500">
          Bill references use format <code className="bg-gray-100 px-1 rounded">{familyBankSettings.businessShortCode || '604020'}#NVIBE20260710R4M</code> (NVIBE2026 + MMDD + 3-char code).
          After saving credentials, set <strong>Default payment method</strong> to Family Bank STK.
        </p>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <SettingsSaveButton
            variant="accent"
            onClick={handleSaveFamilyBank}
            disabled={loadingSettings}
            loading={savingFamilyBank}
          >
            Save Family Bank settings
          </SettingsSaveButton>
        </div>
      </SettingsPanel>
    );
  };

  const renderNcbaPanel = () => {
    if (!isSuperAdmin) {
      return (
        <SettingsPanel
          title="NCBA Till STK"
          description="NCBA Till STK Push via Paybill 880100."
          badge="Restricted"
        >
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
            <p className="font-medium">Super admin access required</p>
          </div>
        </SettingsPanel>
      );
    }

    return (
      <SettingsPanel
        title="NCBA Till (M-Pesa STK)"
        description="Event-specific Till credentials (encrypted). Used only for events with an NCBA override (e.g. Tabasamu Festival). Buyers still see Pay with M-Pesa."
        badge={ncbaReadyForStk ? 'Ready for STK' : (ncbaConfigured ? 'Credentials set' : 'Awaiting setup')}
      >
        <div className="mb-4 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-950">
          <p className="font-semibold mb-1">Bank onboarding</p>
          <p className="text-orange-900/90">
            Paste the Username and Secret Key that were shared with NCBA (email + signed letter). Include Till No(s) and Paybill <strong>880100</strong>.
            These credentials apply only to NCBA-overridden events — not the platform Daraja / Family Bank switcher.
          </p>
        </div>

        {ncbaPlainCredentials && (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
            <p className="font-semibold mb-2">Copy for NCBA email (shown once here)</p>
            <div className="space-y-2 font-mono text-xs break-all">
              <p>Username={ncbaPlainCredentials.username}</p>
              <p>Secret Key={ncbaPlainCredentials.secret}</p>
            </div>
            <button
              type="button"
              onClick={() => copyCallbackUrl(
                `Username=${ncbaPlainCredentials.username}\nSecret Key=${ncbaPlainCredentials.secret}`,
                'NCBA credentials'
              )}
              className="mt-3 rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
            >
              Copy email lines
            </button>
          </div>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          <SettingsField label="Paybill number" hint="NCBA Till uses Paybill 880100">
            <input
              type="text"
              value={ncbaSettings.paybillNo}
              onChange={(e) => setNcbaSettings({ ...ncbaSettings, paybillNo: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Till / AccountNo" hint="Fill after bank assigns the till — required before live STK">
            <input
              type="text"
              value={ncbaSettings.tillNumber || ncbaSettings.accountNo}
              onChange={(e) => setNcbaSettings({
                ...ncbaSettings,
                tillNumber: e.target.value,
                accountNo: e.target.value
              })}
              placeholder="Enter NCBA Till number"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="API base URL">
            <input
              type="text"
              value={ncbaSettings.baseUrl}
              onChange={(e) => setNcbaSettings({ ...ncbaSettings, baseUrl: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            />
          </SettingsField>
          <SettingsField label="Driver" hint="Mock skips HTTP for local testing">
            <select
              value={ncbaSettings.driver}
              onChange={(e) => setNcbaSettings({ ...ncbaSettings, driver: e.target.value })}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
            >
              <option value="live">Live</option>
              <option value="mock">Mock</option>
            </select>
          </SettingsField>
          <SettingsField label="Username" hint="API username shared with the bank (stored encrypted)">
            <input
              type="text"
              value={ncbaSettings.username || ''}
              onChange={(e) => setNcbaSettings({ ...ncbaSettings, username: e.target.value })}
              placeholder="Paste username from bank letter / email"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="off"
            />
          </SettingsField>
          <SettingsField label="Secret Key" hint="Leave as ******** to keep the current encrypted secret">
            <input
              type="password"
              value={ncbaSettings.secret || ''}
              onChange={(e) => setNcbaSettings({ ...ncbaSettings, secret: e.target.value })}
              placeholder="Paste secret key (or leave masked to keep existing)"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm"
              autoComplete="new-password"
            />
          </SettingsField>
        </div>

        {ncbaSettings.credentialsGeneratedAt && (
          <p className="mt-3 text-xs text-gray-500">
            Credentials generated at {new Date(ncbaSettings.credentialsGeneratedAt).toLocaleString()}
          </p>
        )}

        <p className="mt-4 text-xs text-gray-500">
          After Till + credentials are saved, Tabasamu Festival (and any event with an NCBA override) will collect via this Till. Other events keep using Daraja or Family Bank.
        </p>

        <div className="mt-6 pt-6 border-t border-gray-100 flex flex-wrap gap-3">
          <SettingsSaveButton
            variant="accent"
            onClick={handleGenerateNcba}
            disabled={loadingSettings || generatingNcba}
            loading={generatingNcba}
          >
            Generate Username &amp; Secret
          </SettingsSaveButton>
          <button
            type="button"
            onClick={handleRevealNcba}
            disabled={loadingSettings || revealingNcba || !ncbaConfigured}
            className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            {revealingNcba ? 'Revealing…' : 'Reveal for bank email'}
          </button>
          <button
            type="button"
            onClick={handleTestNcba}
            disabled={loadingSettings || testingNcba || !ncbaConfigured}
            className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            {testingNcba ? 'Testing…' : 'Test NCBA auth'}
          </button>
          <SettingsSaveButton
            onClick={handleSaveNcba}
            disabled={loadingSettings}
            loading={savingNcba}
          >
            Save Till &amp; settings
          </SettingsSaveButton>
        </div>
      </SettingsPanel>
    );
  };

  const renderBackupPanel = () => {
    if (!isSuperAdmin) {
      return (
        <SettingsPanel
          title="Site & database backup"
          description="Full zip export of the domain tree and PostgreSQL database."
          badge="Restricted"
        >
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
            <p className="font-medium">Super admin access required</p>
            <p className="mt-2 text-amber-900/90">
              Your current role is <strong>{user?.role || 'unknown'}</strong>. Ask a super admin to run{' '}
              <code className="bg-white/70 px-1 rounded">npm run db:promote-super-admin</code>, then sign in again.
            </p>
          </div>
        </SettingsPanel>
      );
    }

    return (
      <>
        <SettingsPanel
          title="Full site & database backup"
          description="Creates one zip in backups/ with all site files plus database.sql. Runs in the background so the site stays fast."
          badge="Super admin"
        >
          <ul className="text-sm text-gray-600 space-y-2 mb-6">
            <li className="flex gap-2"><span className="text-orange-500">•</span> Includes all site code, assets, and <code className="text-gray-800 bg-gray-100 px-1 rounded">node_modules</code></li>
            <li className="flex gap-2"><span className="text-orange-500">•</span> Skips only the <code className="text-gray-800 bg-gray-100 px-1 rounded">backups/</code> folder (database is added as <code className="text-gray-800 bg-gray-100 px-1 rounded">database.sql</code>)</li>
            <li className="flex gap-2"><span className="text-orange-500">•</span> Saves as <code className="text-gray-800 bg-gray-100 px-1 rounded">nikovibe_backup_YYYYMMDD_HHMMSS.zip</code> in <code className="text-gray-800 bg-gray-100 px-1 rounded">backups/</code></li>
            <li className="flex gap-2"><span className="text-orange-500">•</span> Removes the previous site backup after a successful run</li>
            <li className="flex gap-2"><span className="text-orange-500">•</span> Light compression and low CPU priority to avoid slowing the site</li>
          </ul>

          {siteBackupRunning && (
            <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50/80 p-4">
              <div className="flex items-start gap-3">
                <Loader2 className="h-5 w-5 text-orange-600 animate-spin shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-orange-950">
                    {siteBackupStatus?.message || 'Backup in progress…'}
                  </p>
                  <div className="mt-3 h-2 rounded-full bg-orange-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500"
                      style={{ width: `${Math.max(5, siteBackupStatus?.progress || 5)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-orange-900/80">
                    You can leave this page - we will notify you when it is ready. Usually takes 2–5 minutes (~130 MB).
                  </p>
                </div>
              </div>
            </div>
          )}

          {siteBackupStatus?.status === 'failed' && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              <p className="font-medium">Last backup failed</p>
              <p className="mt-1">{siteBackupStatus.error || siteBackupStatus.message}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleStartSiteBackup}
              disabled={startingSiteBackup || siteBackupRunning}
              className="inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:opacity-50 shadow-sm"
            >
              {startingSiteBackup || siteBackupRunning ? 'Backup running…' : 'Create full site backup'}
            </button>

            {siteBackupRunning && (
              <button
                type="button"
                onClick={handleCancelSiteBackup}
                disabled={cancellingSiteBackup}
                className="inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
              >
                {cancellingSiteBackup ? 'Cancelling…' : 'Cancel backup'}
              </button>
            )}

            {siteBackupReady && (
              <button
                type="button"
                onClick={() => handleDownloadSiteBackup(siteBackupLatest.filename)}
                disabled={downloadingSiteBackup}
                title="Download to your computer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 disabled:opacity-50"
              >
                {downloadingSiteBackup ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download zip
              </button>
            )}
          </div>

          {siteBackupLatest && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Latest site backup on server</h3>
              {siteBackupRoot && (
                <p className="text-xs text-gray-500 mb-3 break-all font-mono bg-gray-50 rounded-lg px-3 py-2">{siteBackupRoot}</p>
              )}
              <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-50/80 rounded-xl border border-gray-100 text-sm">
                <div className="min-w-0">
                  <p className="font-mono text-gray-800 truncate">{siteBackupLatest.filename}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatFileSize(siteBackupLatest.sizeBytes)} · {new Date(siteBackupLatest.createdAt).toLocaleString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownloadSiteBackup(siteBackupLatest.filename)}
                  disabled={downloadingSiteBackup || siteBackupRunning}
                  title="Download to your computer"
                  className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-lg text-orange-700 bg-white border border-orange-200 hover:bg-orange-50 disabled:opacity-50"
                >
                  {downloadingSiteBackup ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}
        </SettingsPanel>

        <div className="mt-6">
          <SettingsPanel
            title="Database only (.sql)"
            description="Quick PostgreSQL export without site files. Also saved under backups/ on the server."
            badge="Optional"
          >
            <button
              type="button"
              onClick={handleDatabaseBackup}
              disabled={backingUp}
              className="inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-800 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              {backingUp ? 'Creating backup…' : 'Download database backup (.sql)'}
            </button>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Saved SQL backups</h3>
              {backupDirectory && (
                <p className="text-xs text-gray-500 mb-3 break-all font-mono bg-gray-50 rounded-lg px-3 py-2">{backupDirectory}</p>
              )}
              {loadingBackups ? (
                <p className="text-sm text-gray-500">Loading backup list…</p>
              ) : savedBackups.length === 0 ? (
                <p className="text-sm text-gray-500">No SQL backups yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden">
                  {savedBackups.map((backup) => (
                    <li key={backup.filename} className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-50/80 text-sm">
                      <div className="min-w-0">
                        <span className="font-mono text-gray-800 truncate block">{backup.filename}</span>
                        <span className="text-gray-500 text-xs mt-1 block">
                          {formatFileSize(backup.sizeBytes)} · {new Date(backup.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDownloadSqlBackup(backup.filename)}
                        disabled={Boolean(downloadingSqlFilename)}
                        title="Download to your computer"
                        className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-lg text-orange-700 bg-white border border-orange-200 hover:bg-orange-50 disabled:opacity-50"
                      >
                        {downloadingSqlFilename === backup.filename ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </SettingsPanel>
        </div>
      </>
    );
  };

  const renderActivePanel = () => {
    switch (activeSection) {
      case 'mail':
        return renderMailPanel();
      case 'payments':
        return renderPaymentsPanel();
      case 'mpesa-daraja':
        return renderMpesaDarajaPanel();
      case 'family-bank':
        return renderFamilyBankPanel();
      case 'ncba-till':
        return renderNcbaPanel();
      case 'backup':
        return renderBackupPanel();
      case 'maps':
      default:
        return renderMapsPanel();
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AdminSidebar isOpen={navSidebarOpen} onClose={() => setNavSidebarOpen(false)} />

      <div className="lg:pl-72">
        <AdminHeader onMenuClick={() => setNavSidebarOpen(true)} title="Settings" />

        <main className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
          <div className="mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-2">
              Administration
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">System settings</h1>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Configure integrations, payments, email delivery, and platform maintenance.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Settings section nav - mobile: horizontal scroll */}
            <aside className="lg:w-64 shrink-0">
              <div className="lg:hidden mb-2">
                <label htmlFor="settings-section-mobile" className="sr-only">Settings section</label>
                <select
                  id="settings-section-mobile"
                  value={activeSection}
                  onChange={(e) => selectSection(e.target.value)}
                  disabled={!roleResolved}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm disabled:opacity-60"
                >
                  {visibleSections.flatMap((g) => g.items.map((item) => (
                    <option key={item.id} value={item.id}>{g.label} - {item.label}</option>
                  )))}
                </select>
              </div>
              <div className="hidden lg:block rounded-2xl border border-gray-200/80 bg-white/60 backdrop-blur-sm p-3 shadow-sm min-h-[12rem]">
                {roleResolved ? (
                  <SettingsSectionNav
                    activeId={activeSection}
                    onSelect={selectSection}
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                  />
                ) : (
                  <p className="px-3 py-4 text-sm text-gray-500">Loading settings…</p>
                )}
              </div>
            </aside>

            {/* Active panel */}
            <div className="flex-1 min-w-0">
              {sectionMeta && (
                <p className="hidden lg:block text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                  {sectionMeta.groupLabel}
                </p>
              )}
              {renderActivePanel()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
